import { get, isEmpty, last } from 'lodash';
import {
    ACTIVATION_PRODUCTS_SELECTION_METHOD_UNLIMITED,
    ACTIVATION_PRODUCTS_LIMITATION_METHOD_NONE,
    ACTIVATION_PRODUCTS_SELECTION_METHOD_PRESELECTED,
    ACTIVATION_PRODUCTS_SELECTION_METHOD_LIMIT_AMOUNT,
    ACTIVATION_PRODUCTS_SELECTION_METHOD_WEBSITE,
    ACTIVATION_PRODUCTS_LIMITATION_METHOD_PRODUCTS,
    ACTIVATION_PRODUCTS_LIMITATION_METHOD_CATEGORIES,
    ACTIVATION_PRODUCTS_LIMITATION_METHOD_SLOTS
} from '../../../../constants/activation-products';
import { pluralize } from '../../../../helpers/string-helpers';
import { formatCurrency } from '../../../../helpers/formatters';
import { useFeatureFlagsStore } from '../../../../../stores';

export default {
    props: {
        brief: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            backgroundImagePlaceholder: 'https://d1igyfcg6nq1cr.cloudfront.net/placeholder/placeholder-square.jpg'
        };
    },
    computed: {
        productConfig: {
            cache: false,
            get() {
                return last(get(this, 'brief.product_configs') || []) || {};
            }
        },
        findProductConfigItems() {
            return this.productConfig.items || [];
        },
        hasProductConfigItems() {
            return this.productConfig.items.length > 0;
        },
        hasProductConfig() {
            return !isEmpty(this.productConfig);
        },
        mapProductConfigItemValues() {
            return this.productConfig.items.map((item) =>
                item.product_option_values.filter((option) => option.value !== -1)
            );
        },
        showPrices() {
            return this.productConfig.show_prices;
        },
        selectionMethodUnlimited() {
            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_UNLIMITED;
        },
        selectionMethodLimitAmount() {
            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_LIMIT_AMOUNT;
        },
        selectionMethodPreselected() {
            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_PRESELECTED;
        },
        selectionMethodWebsite() {
            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_WEBSITE
                && !this.selectionMethodWebsiteShopify;
        },
        selectionMethodWebsiteShopifySharedCart() {
            const isEnabled = useFeatureFlagsStore().data['product-select-from-website-q2-2024'] || false;
            const ecommerceAccountProvider = get(this, 'brief.brand.ecommerce_account.provider');

            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_WEBSITE
                && ecommerceAccountProvider === 'shopify'
                && isEnabled;
        },
        selectionMethodWebsiteShopify() {
            if (this.selectionMethodWebsiteShopifySharedCart) {
                return false;
            }
            const isEnabled = useFeatureFlagsStore().data['new-product-select-from-website'] || false;
            const ecommerceAccountProvider = get(this, 'brief.brand.ecommerce_account.provider');

            return this.productConfig.selection_method === ACTIVATION_PRODUCTS_SELECTION_METHOD_WEBSITE
                && ecommerceAccountProvider === 'shopify'
                && isEnabled;
        },
        limitationMethodNone() {
            return this.productConfig.limitation_method === ACTIVATION_PRODUCTS_LIMITATION_METHOD_NONE;
        },
        limitationMethodProducts() {
            return this.productConfig.limitation_method === ACTIVATION_PRODUCTS_LIMITATION_METHOD_PRODUCTS;
        },
        limitationMethodCategories() {
            return this.productConfig.limitation_method === ACTIVATION_PRODUCTS_LIMITATION_METHOD_CATEGORIES;
        },
        limitationMethodSlots() {
            return this.productConfig.limitation_method === ACTIVATION_PRODUCTS_LIMITATION_METHOD_SLOTS;
        },
        priceLimitEnabled() {
            return this.productConfig.price_limit && this.productConfig.price_limit > 0;
        },
        productsSelected() {
            return this.selection?.length;
        },
        limitAndWebsiteText() {
            let priceLimit = '';
            if (this.priceLimitEnabled) {
                priceLimit =
                    ' up to ' +
                    formatCurrency(this.productConfig.price_limit, get(this, 'brief.brand.currency', 'usd'));
            }
            return `You will receive ${this.productConfig.quantity_limit} ${pluralize(
                this.productConfig.quantity_limit,
                'product'
            )}${priceLimit}.`;
        },
        getConfigText() {
            const customized = get(this, 'brief.activation.customization.products_short_description');

            if (customized) {
                return customized;
            }

            const priceLimit = formatCurrency(this.productConfig.price_limit, get(this, 'brief.brand.currency', 'usd'));

            const configMap = {
                [ACTIVATION_PRODUCTS_SELECTION_METHOD_LIMIT_AMOUNT]: this.limitAndWebsiteText,
                [ACTIVATION_PRODUCTS_SELECTION_METHOD_WEBSITE]: this.limitAndWebsiteText,
                [ACTIVATION_PRODUCTS_SELECTION_METHOD_UNLIMITED]:
                    `Choose as many products as you'd like` +
                    (this.productConfig.price_limit > 0 ? `, up to ${priceLimit}.` : '.'),
                [ACTIVATION_PRODUCTS_SELECTION_METHOD_PRESELECTED]: `You'll receive specific products`
            };
            return configMap[this.productConfig.selection_method];
        }
    },
    methods: {
        imageOrPlaceholder(url) {
            return `url('${url ? url : this.backgroundImagePlaceholder}')`;
        },
        mapProductConfigItems(key) {
            return this.findProductConfigItems.map((item) => item[key]);
        },
        getProductConfigItem(key, value, slot, productIdx) {
            const configItems = slot
                ? get(
                      this.findProductConfigItems.find((item) => item.id === slot),
                      'items',
                      []
                  )
                : this.findProductConfigItems;

            return configItems[productIdx] || configItems.find((item) => item[key] === value) || {};
        }
    }
};
